import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Flame, Droplets, Zap, Radio, Thermometer, ShieldCheck } from 'lucide-react';

const Products = () => {
  const navigate = useNavigate();
  
  const products = [
    {
      title: "Fire Extinguishers",
      icon: Flame,
      desc: "Complete range of portable and wheeled units.",
      bg: "bg-gradient-to-br from-red-500/20 to-red-900/40",
      colSpan: "md:col-span-2 lg:col-span-2",
      rowSpan: "md:row-span-2"
    },
    {
      title: "Sprinkler Systems",
      icon: Droplets,
      desc: "Automatic and reliable suppression.",
      bg: "bg-gradient-to-br from-blue-500/20 to-blue-900/40",
      colSpan: "md:col-span-1 lg:col-span-1",
      rowSpan: "md:row-span-1"
    },
    {
      title: "Detection Systems",
      icon: Zap,
      desc: "Smart sensors and early warning.",
      bg: "bg-gradient-to-br from-yellow-500/20 to-yellow-900/40",
      colSpan: "md:col-span-1 lg:col-span-1",
      rowSpan: "md:row-span-1"
    },
    {
      title: "Alarm Systems",
      icon: Radio,
      desc: "Comprehensive notification systems.",
      bg: "bg-gradient-to-br from-orange-500/20 to-orange-900/40",
      colSpan: "md:col-span-2 lg:col-span-1",
      rowSpan: "md:row-span-1"
    },
    {
      title: "Monitoring Equipment",
      icon: Thermometer,
      desc: "Environmental monitoring and logging.",
      bg: "bg-gradient-to-br from-green-500/20 to-green-900/40",
      colSpan: "md:col-span-1 lg:col-span-1",
      rowSpan: "md:row-span-1"
    },
    {
      title: "Safety Gear",
      icon: ShieldCheck,
      desc: "Personal protective equipment.",
      bg: "bg-gradient-to-br from-purple-500/20 to-purple-900/40",
      colSpan: "md:col-span-1 lg:col-span-2",
      rowSpan: "md:row-span-1"
    }
  ];

  return (
    <section id="products" className="py-24 bg-dark relative overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1 rounded-full bg-white/5 text-primary font-semibold text-sm mb-6 uppercase tracking-wider border border-white/10"
            >
              Equipment Catalog
            </motion.div>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold font-barlow text-white uppercase tracking-tight"
            >
              Premium <span className="text-primary">Products</span>
            </motion.h2>
          </div>
          
          <motion.button 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onClick={() => navigate('/catalog')}
            className="flex items-center gap-2 text-white font-semibold hover:text-primary transition-colors duration-300 pb-2 border-b-2 border-primary/30 hover:border-primary"
          >
            View Full Catalog <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 auto-rows-[250px]">
          {products.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className={`relative group rounded-2xl overflow-hidden border border-white/10 ${item.colSpan} ${item.rowSpan} ${item.bg}`}
              >
                {/* Background Base */}
                <div className="absolute inset-0 bg-dark/40 group-hover:bg-dark/20 transition-colors duration-500" />
                
                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                  <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center mb-auto border border-white/20 transform group-hover:-translate-y-2 group-hover:bg-primary/80 transition-all duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <div className="transform transition-transform duration-500 group-hover:-translate-y-1">
                    <h3 className="text-2xl font-bold text-white font-barlow tracking-wide mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-sm opacity-90 transition-opacity duration-500">
                      {item.desc}
                    </p>
                  </div>
                </div>

                {/* Hover Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              </motion.div>
            )
          })}
        </div>

      </div>
    </section>
  );
};

export default Products;
